import React, { useState, useEffect, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import "./inventory.css";
import {
  Boxes,
  Plus,
  Trash2,
  Save as SaveIcon,
  Calculator,
  Package,
} from "lucide-react";

export default function Inventory() {
  const { companyId } = useCompany();
  const [rows, setRows] = useState([]);

  // 1️⃣ Load existing inventory items (UNCHANGED)
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const { data, error } = await supabase
        .from("inventory")
        .select("id, description, amount, quantity")
        .eq("company_id", companyId)
        .order("id", { ascending: true });

      if (error) {
        console.error("Error loading inventory:", error);
        return;
      }
      if (data?.length) {
        setRows(data);
      } else {
        setRows([{ id: null, description: "", amount: 0, quantity: 0 }]);
      }
    })();
  }, [companyId]);

  // ➕ Add a blank row (UNCHANGED)
  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { id: null, description: "", amount: 0, quantity: 0 },
    ]);
  };

  // ✏️ Handle field changes (UNCHANGED)
  const handleChange = (idx, field, value) => {
    setRows((prev) =>
      prev.map((r, i) =>
        i === idx
          ? {
              ...r,
              [field]: field === "description" ? value : Number(value) || 0,
            }
          : r
      )
    );
  };

  // 🗑️ Delete a row (DB + UI) (UNCHANGED)
  const deleteRow = async (idx) => {
    const row = rows[idx];
    if (row.id) {
      const { error } = await supabase
        .from("inventory")
        .delete()
        .eq("id", row.id)
        .eq("company_id", companyId);
      if (error) console.error("Error deleting item:", error);
    }
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  // 💾 Save (UNCHANGED logic)
  const saveData = async () => {
    if (!companyId) return;

    // 1) get the current user
    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();
    if (authErr || !user) {
      return alert("Please sign in to save.");
    }
    const userId = user.id;

    // 2) payload—unchanged schema
    const payload = rows.map((r) => ({
      id: r.id || uuidv4(),
      company_id: companyId,
      user_id: userId,
      description: r.description,
      amount: r.amount,
      quantity: r.quantity,
    }));

    // 3) upsert on id
    const { error: upsertError } = await supabase
      .from("inventory")
      .upsert(payload, { onConflict: ["id"] });
    if (upsertError) {
      console.error("Upsert error:", upsertError);
      return alert("Error saving inventory.");
    }

    // 4) remove stale ones
    const { data: existing, error: fetchErr } = await supabase
      .from("inventory")
      .select("id")
      .eq("company_id", companyId);
    if (fetchErr) throw fetchErr;

    const keepIds = payload.map((r) => r.id);
    const staleIds = (existing || [])
      .map((r) => r.id)
      .filter((id) => !keepIds.includes(id));

    for (const id of staleIds) {
      const { error: delErr } = await supabase
        .from("inventory")
        .delete()
        .eq("id", id);
      if (delErr) console.error("Error deleting stale item:", delErr);
    }

    alert("Resources saved!");
  };

  // —— UI helpers (derived only) ——
  const totalSkus = rows.length;
  const totalUnits = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.quantity) || 0), 0),
    [rows]
  );
  const totalStockValue = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [rows]
  );

  const unitCost = (row) => {
    const q = Number(row.quantity) || 0;
    const a = Number(row.amount) || 0;
    return q > 0 ? a / q : 0;
  };

  const onRowKeyDown = (e, idx) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (idx === rows.length - 1) addRow();
    }
  };

  return (
    <div className="inv-shell">
      {/* Header */}
      <header className="panel inv-head">
        <div className="title">
          <h2>
            <Boxes size={18} /> Resources
          </h2>
          <p className="muted">
            Manage resources/items used in estimates. <strong>Amount</strong> is
            the total cost for the stock, <strong>Quantity</strong> is units on
            hand. The app will compute <em>Unit Cost</em> from these.
          </p>
        </div>
        <div className="actions">
          <button className="btn-ghost" onClick={addRow}>
            <Plus size={16} /> Add Item
          </button>
          <button className="btn-primary" onClick={saveData}>
            <SaveIcon size={16} /> Save Resources
          </button>
        </div>
      </header>

      {/* Metrics */}
      <section className="panel inv-metrics">
        <div className="metric">
          <span className="label">
            <Package size={14} /> Total RSUs
          </span>
          <span className="value">{totalSkus}</span>
        </div>
        <div className="metric">
          <span className="label">
            <Calculator size={14} /> Total Units
          </span>
          <span className="value">{totalUnits.toLocaleString()}</span>
        </div>
        <div className="metric">
          <span className="label">Resource Value</span>
          <span className="value">
            ₦
            {totalStockValue.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      </section>

      {/* Table */}
      <section className="panel table-card">
        <div className="table-wrap">
          <table className="inv-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ width: 180 }}>Quantity</th>
                <th style={{ width: 220 }}>Amount (₦) — Total</th>
                <th style={{ width: 200 }}>Unit Cost (₦)</th>
                <th style={{ width: 110 }} className="center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map((r, i) => (
                  <tr key={r.id || i}>
                    <td>
                      <input
                        placeholder="e.g., Excavator rental, Senior Engineer, Cable trunking"
                        value={r.description}
                        onChange={(e) =>
                          handleChange(i, "description", e.target.value)
                        }
                        onKeyDown={(e) => onRowKeyDown(e, i)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        inputMode="numeric"
                        placeholder="0"
                        value={r.quantity}
                        onChange={(e) =>
                          handleChange(i, "quantity", e.target.value)
                        }
                        onKeyDown={(e) => onRowKeyDown(e, i)}
                      />
                    </td>
                    <td>
                      <div className="input-prefix">
                        <span className="prefix">₦</span>
                        <input
                          type="number"
                          inputMode="decimal"
                          placeholder="0.00"
                          value={r.amount}
                          onChange={(e) =>
                            handleChange(i, "amount", e.target.value)
                          }
                          onKeyDown={(e) => onRowKeyDown(e, i)}
                        />
                      </div>
                    </td>
                    <td className="unit-cost">
                      ₦
                      {unitCost(r).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="center">
                      <button
                        type="button"
                        className="icon-btn danger"
                        onClick={() => deleteRow(i)}
                        aria-label="Delete item"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="empty-state">
                    No items. Click <strong>Add Item</strong> to start.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer actions */}
        <div className="table-actions">
          <button className="btn-ghost" onClick={addRow}>
            <Plus size={16} /> Add Item
          </button>
          <button className="btn-primary" onClick={saveData}>
            <SaveIcon size={16} /> Save Resource
          </button>
        </div>
      </section>
    </div>
  );
}
